// MindForge web — localStorage adapter for PlayerState saves.
//
// Per the day-4 spec, saves are keyed by the Basic Auth username so multiple
// testers on the same browser stay completely separate. Username comes from
// the nginx-injected <meta name="x-mindforge-user"> (see hooks/useUser.ts).
//
// Anonymous users (auth bypassed in local dev) fall back to a shared
// "mindforge.save.anonymous" key.

import { PlayerState } from "./playerState";

const SAVE_KEY_PREFIX = "mindforge.save.";

function saveKey(username: string): string {
  return SAVE_KEY_PREFIX + (username.trim() || "anonymous");
}

/** Load this tester's save from localStorage, or return a fresh state. */
export function loadPlayerState(username: string): PlayerState {
  try {
    const raw = localStorage.getItem(saveKey(username));
    if (!raw) return new PlayerState();
    return PlayerState.fromJSON(JSON.parse(raw));
  } catch {
    // Corrupted JSON or localStorage error — start fresh, don't crash.
    return new PlayerState();
  }
}

/** Write this tester's save to localStorage. Synchronous, no atomic write needed. */
export function savePlayerState(state: PlayerState, username: string): void {
  try {
    localStorage.setItem(saveKey(username), JSON.stringify(state.toJSON()));
  } catch {
    // Quota exceeded or storage unavailable. Beta-only, swallow rather than
    // crashing the session — testers can always reset via the UI button.
  }
}

/** Wipe this tester's save (for the Reset Progress button). */
export function clearPlayerState(username: string): void {
  try {
    localStorage.removeItem(saveKey(username));
  } catch {
    // Same swallow rationale as savePlayerState.
  }
}

const ONBOARD_KEY_PREFIX = "mindforge.onboarded.";

/** Whether this tester has already seen the first-run onboarding. */
export function hasOnboarded(username: string): boolean {
  try {
    return localStorage.getItem(ONBOARD_KEY_PREFIX + (username.trim() || "anonymous")) === "1";
  } catch {
    return false;
  }
}

/** Mark the first-run onboarding as seen for this tester. */
export function markOnboarded(username: string): void {
  try {
    localStorage.setItem(ONBOARD_KEY_PREFIX + (username.trim() || "anonymous"), "1");
  } catch {
    // Swallow — onboarding will just show again next time, harmless.
  }
}
